/**
 * Chat Options Registry
 *
 * Frontend registry for plugin-provided chat option flags.
 * Plugins register boolean/enum flags that the chat page renders
 * as toggles in the sidebar and passes along on every send.
 *
 * Core ships with "useRag" as the only built-in flag.
 * All other flags (e.g. "includeMemory") are registered by plugins.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// ============================================================
// Types
// ============================================================

export interface ChatOptionDefinition {
  /** Unique key sent to the backend (e.g. 'includeMemory') */
  id: string;
  /** Plugin that registered this option */
  pluginId: string;
  /** Display label */
  label: string;
  /** Tooltip / longer description */
  description?: string;
  /** Type of control */
  type: 'boolean' | 'select';
  /** Default value */
  defaultValue: boolean | string;
  /** For 'select' type — dropdown choices */
  options?: Array<{ label: string; value: string }>;
  /** Sort order (lower = first) */
  order?: number;
  /** Icon emoji */
  icon?: string;
}

export interface ChatOptionValue {
  id: string;
  value: boolean | string;
}

// ============================================================
// State
// ============================================================

interface ChatOptionsState {
  /** Registered option definitions */
  definitions: ChatOptionDefinition[];
  /** Current runtime values (id → value) */
  values: Record<string, boolean | string>;

  // ---- mutations ----
  registerOption: (def: ChatOptionDefinition) => void;
  unregisterOption: (id: string) => void;
  unregisterPluginOptions: (pluginId: string) => void;
  setValue: (id: string, value: boolean | string) => void;
  getValues: () => Record<string, boolean | string>;
  reset: () => void;
}

// ============================================================
// Store
// ============================================================

export const useChatOptionsRegistry = create<ChatOptionsState>()(
  subscribeWithSelector((set, get) => ({
    definitions: [],
    values: {},

    registerOption(def) {
      set((state) => {
        const filtered = state.definitions.filter((d) => d.id !== def.id);
        const sorted = [...filtered, def].sort(
          (a, b) => (a.order ?? 100) - (b.order ?? 100),
        );
        const values = { ...state.values };
        if (!(def.id in values)) {
          values[def.id] = def.defaultValue;
        }
        return { definitions: sorted, values };
      });
    },

    unregisterOption(id) {
      set((state) => {
        const definitions = state.definitions.filter((d) => d.id !== id);
        const values = { ...state.values };
        delete values[id];
        return { definitions, values };
      });
    },

    unregisterPluginOptions(pluginId) {
      set((state) => {
        const toRemove = new Set(
          state.definitions
            .filter((d) => d.pluginId === pluginId)
            .map((d) => d.id),
        );
        const definitions = state.definitions.filter(
          (d) => d.pluginId !== pluginId,
        );
        const values = { ...state.values };
        for (const id of toRemove) delete values[id];
        return { definitions, values };
      });
    },

    setValue(id, value) {
      set((state) => ({
        values: { ...state.values, [id]: value },
      }));
    },

    getValues() {
      return { ...get().values };
    },

    reset() {
      set({ definitions: [], values: {} });
    },
  })),
);
